import React, { useEffect, useMemo, useRef, useState } from "react";
import Table, { TableLink } from "../../components/Table";
import Sidebar from "../../components/Sidebar";
import {
  AppliedFilterLabel,
  AppliedFilterPill,
  AppliedFiltersBar,
  ContentWrapper,
  LeadNoteButton,
  LeadsPageContainer,
  RemoveAppliedFilterButton,
  StatusAndDeleteIconContainer,
  Title,
} from "./LeadPage.styles";
import {
  fetchUserId,
  fetchCourses,
  getLeads,
  selectCourseForLead,
  deleteLead,
  fetchLeadRoles,
  updateLeadNote,
} from "../../services/leadService/leadService";
import { Course, LeadData } from "../../services/leadService/leadService.types";
import DropdownInCell from "../../components/Dropdowns/DropdownInCell/DropdownInCell";
import IconButton from "../../components/Buttons/IconButton";
import DeletedRow from "../../components/DeletedRow";
import { ConfirmModal } from "../../components/Modals";
import { TableProps } from "../../components/Table/Table.types";
import { useNotify } from "context/NotificationContext";
import { logger } from "utils/logger";
import { useListPage } from "hooks/useListPage";
import { getServerErrorData, getStatusCode } from "utils/httpError";
import { checkedIcon, delIcon } from "assets/icons";
import LeadNoteModal from "./LeadNoteModal";

const countRolesInPage = (items: LeadData[]) =>
  items.reduce<Record<string, number>>((counts, lead) => {
    if (lead.role_name) {
      counts[lead.role_name] = (counts[lead.role_name] ?? 0) + 1;
    }
    return counts;
  }, {});

const LeadsPage: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [deleteLeadId, setDeleteLeadId] = useState<number | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [noteModalLead, setNoteModalLead] = useState<LeadData | null>(null);
  const [isNoteSaving, setIsNoteSaving] = useState(false);
  const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const toast = useNotify();

  const [filterStartDate, setFilterStartDate] = useState<Date | null>(new Date());
  const [filterEndDate, setFilterEndDate] = useState<Date | null>(null);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [roles, setRoles] = useState<{ label: string; value: string }[]>([]);
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [roleCounts, setRoleCounts] = useState<Record<string, number>>({});

  const appliedRoleValues = useMemo(() => filters["role"] ?? [], [filters]);

  const {
    items: leads,
    setItems: setLeads,
    loading,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    handleSort,
    reload: loadLeads,
  } = useListPage<LeadData, Awaited<ReturnType<typeof getLeads>>>({
    fetcher: ({ page, pageSize: size, orderBy }) =>
      getLeads(
        page,
        size,
        orderBy,
        filterStartDate ? filterStartDate.toISOString().split("T")[0] : undefined,
        filterEndDate ? filterEndDate.toISOString().split("T")[0] : undefined,
        appliedRoleValues.length ? appliedRoleValues.join(",") : undefined,
      ),
    select: (response) => ({ items: response.results, total: response.count }),
    onLoaded: (response) => {
      if (appliedRoleValues.length === 0) {
        setRoleCounts(countRolesInPage(response.results));
      }
    },
    errorMessage: "Не удалось загрузить лидов",
    errorStateMessage: "Не удалось загрузить лидов.",
  });

  const roleOptions = useMemo(
    () =>
      roles.map((role) => ({
        ...role,
        count: roleCounts[role.label] ?? 0,
      })),
    [roleCounts, roles],
  );

  const appliedRoles = useMemo(
    () =>
      appliedRoleValues.map((value) => ({
        value,
        label: roles.find((role) => role.value === value)?.label ?? value,
      })),
    [appliedRoleValues, roles],
  );

  useEffect(() => {
    loadCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFirstFilterRun = useRef(true);
  useEffect(() => {
    if (isFirstFilterRun.current) {
      isFirstFilterRun.current = false;
      return;
    }
    loadLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, filterStartDate, filterEndDate]);

  const handleFilterSave = (key: string, selectedValues: string[]) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: selectedValues,
    }));
    setCurrentPage(1);
    setActiveFilter(null);
  };

  const handleFilterReset = (key: string) => {
    setFilters((prevFilters) => {
      const updatedFilters = { ...prevFilters };
      delete updatedFilters[key];
      return updatedFilters;
    });
    setCurrentPage(1);
    setActiveFilter(null);
  };

  const handleRemoveRoleFilter = (value: string) => {
    const nextValues = appliedRoleValues.filter((item) => item !== value);
    if (nextValues.length) {
      handleFilterSave("role", nextValues);
    } else {
      handleFilterReset("role");
    }
  };

  const loadCourses = async () => {
    try {
      const userId = await fetchUserId();
      const coursesData = await fetchCourses({ userId });
      setCourses(
        coursesData.map((course) => ({ id: Number(course.id), title: course.title })),
      );
    } catch (err) {
      logger.error("Ошибка при загрузке курсов:", err);
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось загрузить курсы",
        life: 3000,
      });
    }
  };

  const handleCourseChange = async (
    leadId: number,
    selectedCourseId: string | number,
  ) => {
    const previousLeads = [...leads];
    setLeads((prevLeads) =>
      prevLeads.map((lead) =>
        lead.id === leadId
          ? { ...lead, selected_course: selectedCourseId }
          : lead,
      ),
    );

    try {
      await selectCourseForLead(leadId, Number(selectedCourseId));
      toast.show({
        severity: "success",
        summary: "Успешно",
        detail: `Курс успешно изменён для лида ${leadId}`,
        life: 3000,
      });
    } catch (error) {
      logger.error("Ошибка при обновлении курса для лида:", error);
      setLeads(previousLeads);
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail:
          getStatusCode(error) === 403
            ? "Для выбора курса для лида необходимо продлить подписку"
            : `Не удалось установить курс для лида: ${
                getServerErrorData(error)?.error ?? error
              }`,
        life: getStatusCode(error) === 403 ? 5000 : 3000,
      });
    }
  };

  const handleDeleteLead = (leadId: number) => {
    setPendingDelete(leadId);
    deleteTimeoutRef.current = setTimeout(async () => {
      try {
        await deleteLead(leadId);
        setPendingDelete(null);
        loadLeads();
      } catch (error) {
        logger.error("Ошибка при удалении лида:", error);
        toast.show({
          severity: "error",
          summary: "Ошибка",
          detail: `Ошибка удаления лида ${leadId}`,
          life: 3000,
        });
      }
    }, 5000);
  };

  const confirmDelete = (leadId: number) => {
    setDeleteLeadId(leadId);
    setIsConfirmModalOpen(true);
  };

  const handleConfirm = () => {
    if (deleteLeadId !== null) {
      handleDeleteLead(deleteLeadId);
    }
    setIsConfirmModalOpen(false);
    setDeleteLeadId(null);
  };

  const handleCancel = () => {
    setIsConfirmModalOpen(false);
    setDeleteLeadId(null);
  };

  const restoreLead = (leadId: number) => {
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
      deleteTimeoutRef.current = null;
    }
    setPendingDelete(null);
    toast.show({
      severity: "info",
      summary: "Восстановление",
      detail: "Удаление лида отменено",
      life: 3000,
    });
  };

  const handleCalendarChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setFilterStartDate(start);
    setFilterEndDate(end);
    if (start && end) {
      setActiveFilter(null);
    }
  };

  useEffect(() => {
    loadRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRoles = async () => {
    try {
      const rolesData = await fetchLeadRoles();
      setRoles(
        rolesData.map((role) => ({ label: role.name, value: String(role.id) })),
      );
    } catch (err) {
      logger.error("Ошибка при загрузке ролей:", err);
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось загрузить роли",
        life: 3000,
      });
    }
  };

  const persistLeadNote = async (note: string, deleted = false) => {
    const leadId = noteModalLead?.id;
    if (leadId === undefined) return;

    setIsNoteSaving(true);
    try {
      await updateLeadNote(leadId, note);
      setLeads((previous) =>
        previous.map((lead) => (lead.id === leadId ? { ...lead, note } : lead)),
      );
      toast.show({
        severity: "success",
        summary: deleted ? "Заметка удалена" : "Успешно",
        detail: deleted ? "Заметка была успешно удалена." : "Заметка успешно обновлена.",
        life: 3000,
      });
      setNoteModalLead(null);
    } catch (error) {
      logger.error("Ошибка при сохранении заметки лида:", error);
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: deleted ? "Не удалось удалить заметку." : "Не удалось обновить заметку.",
        life: 3000,
      });
    } finally {
      setIsNoteSaving(false);
    }
  };

  const headers: TableProps["headers"] = [
    { label: "Фамилия", key: "surname", fieldName: "surname" },
    { label: "Имя", key: "name", fieldName: "name" },
    { label: "Телеграм", key: "telegram", fieldName: "telegram" },
    {
      label: "Дата заявки",
      key: "created",
      fieldName: "created",
      isFilter: true,
      filterType: "calendar",
    },
    { label: "Телефон", key: "phone", fieldName: "phone" },
    {
      label: "Роль",
      key: "role",
      fieldName: "role",
      isFilter: true,
      filterType: "list",
      listOptions: roleOptions,
    },
    { label: "Курс", key: "course", fieldName: "course__title" },
    { label: "Заметки", key: "notes", fieldName: "notes", sortable: false },
    {
      label: "Курс/группа",
      key: "selected_course",
      fieldName: "selected_course__title",
    },
    {
      label: "",
      key: "statusAndDelete",
      fieldName: "statusAndDelete",
      sortable: false,
    },
  ];

  const rows = leads.map((lead) => {
    if (lead.id === pendingDelete) {
      return {
        name: (
          <DeletedRow
            id={lead.id}
            onRestore={restoreLead}
            colSpan={9}
            message="Лид будет удалён через 5 секунд."
            targetText="Отменить"
          />
        ),
      };
    }

    return {
      surname: lead.surname,
      name: lead.name,
      telegram: lead.telegram ? (
        <TableLink href={`https://t.me/${lead.telegram}`} external>
          @{lead.telegram}
        </TableLink>
      ) : (
        "-"
      ),
      created: lead.created ? new Date(lead.created).toLocaleDateString() : "-",
      phone: lead.phone,
      role: lead.role_name || "-",
      course: lead.course_name || "-",
      notes: (
        <LeadNoteButton type="button" onClick={() => setNoteModalLead(lead)}>
          Редактировать
        </LeadNoteButton>
      ),
      selected_course: lead.selected_course ? (
        <>
          {courses.find(
            (course) => String(course.id) === String(lead.selected_course),
          )?.title || "Курс не найден"}
        </>
      ) : (
        <DropdownInCell
          options={courses.map((course) => ({
            label: course.title,
            value: String(course.id),
          }))}
          value={String(lead.selected_course)}
          onChange={(selectedValue) => {
            if (lead.id !== undefined) {
              handleCourseChange(lead.id, selectedValue);
            } else {
              logger.error("ID лида отсутствует");
            }
          }}
        />
      ),
      statusAndDelete: (
        <StatusAndDeleteIconContainer>
          {lead.selected_course && (
            <IconButton icon={checkedIcon} altText="Одобрить" />
          )}
          <IconButton
            icon={delIcon}
            tone="danger"
            altText="Удалить"
            onClick={() => {
              if (lead.id !== undefined) {
                confirmDelete(lead.id);
              } else {
                logger.error("ID лида отсутствует");
              }
            }}
          />
        </StatusAndDeleteIconContainer>
      ),
    };
  });

  return (
    <LeadsPageContainer>
      <Sidebar />
      <ContentWrapper $roleFilterActive={appliedRoleValues.length > 0}>
        <Title>Список лидов</Title>

        {appliedRoles.length > 0 && (
          <AppliedFiltersBar aria-label="Применённые фильтры по роли">
            <AppliedFilterLabel>Роль:</AppliedFilterLabel>
            {appliedRoles.map((role) => (
              <AppliedFilterPill key={role.value}>
                {role.label}
                <RemoveAppliedFilterButton
                  type="button"
                  aria-label={`Убрать ${role.label}`}
                  onClick={() => handleRemoveRoleFilter(role.value)}
                />
              </AppliedFilterPill>
            ))}
          </AppliedFiltersBar>
        )}

        <Table
          emptyState={{
            title: "Заявок пока нет",
            description:
              "Здесь появятся заявки, оставленные через форму на вашей странице.",
          }}
          headers={headers}
          rows={rows}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          onSort={handleSort}
          loading={loading}
          rowAmountOptions={[
            { label: "7 лидов", value: 7 },
            { label: "15 лидов", value: 15 },
            { label: "20 лидов", value: 20 },
          ]}
          specialRowKey="name"
          onCalendarChange={handleCalendarChange}
          activeFilter={activeFilter}
          filterStartDate={filterStartDate || undefined}
          filterEndDate={filterEndDate || undefined}
          handleActiveFilterChange={setActiveFilter}
          appliedFilters={filters}
          onFilterSave={handleFilterSave}
          onFilterReset={handleFilterReset}
        />
      </ContentWrapper>

      <LeadNoteModal
        isOpen={noteModalLead !== null}
        initialNote={noteModalLead?.note || ""}
        isSaving={isNoteSaving}
        onClose={() => {
          if (!isNoteSaving) setNoteModalLead(null);
        }}
        onSave={(note) => persistLeadNote(note)}
        onDelete={() => persistLeadNote("", true)}
      />

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        title="Подтвердите удаление"
        message="Вы уверены, что хотите удалить лид?"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </LeadsPageContainer>
  );
};

export default LeadsPage;
